// Upload/replace/download widget for one product's TDS or MSDS file, via the
// generic POST /api/files/upload (category "catalog-document") + GET
// /api/files/download. Shared between ProductForm.tsx (per-product edit) and
// WebsiteDocumentsPage.tsx (the library view across every product).
import { FileText, Upload, Download, X } from 'lucide-react';

export interface CatalogDocument {
  storageKey: string;
  originalName: string;
  mimeType?: string;
  size?: number;
  uploadedAt?: string;
}

/** A product's stored tdsDocument/msdsDocument sub-object (server field names)
 *  -> the shape this component and its upload handler use. */
export function toCatalogDocument(doc: any): CatalogDocument | null {
  if (!doc?.storageKey) return null;
  return {
    storageKey: doc.storageKey,
    originalName: doc.originalname || doc.filename || 'Uploaded file',
    mimeType: doc.mimeType,
    size: doc.size,
    uploadedAt: doc.uploadedAt,
  };
}

export function CatalogDocumentSlot({ label, doc, uploading, onUpload, onClear }: {
  label: string;
  doc: CatalogDocument | null;
  uploading: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
}) {
  const inputId = `catalog-doc-${label.replace(/\W+/g, '-').toLowerCase()}-${Math.random().toString(36).slice(2, 7)}`;
  return (
    <div className="rounded-lg border border-amber-300 bg-white dark:bg-amber-950/20 dark:border-amber-800 p-3">
      <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 mb-2">{label}</p>
      {doc ? (
        <div className="flex items-center justify-between gap-2">
          <a
            href={`/api/files/download?key=${encodeURIComponent(doc.storageKey)}`}
            target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 text-sm text-amber-900 dark:text-amber-200 hover:underline min-w-0"
          >
            <FileText className="h-4 w-4 shrink-0" />
            <span className="truncate">{doc.originalName || 'Uploaded file'}</span>
            <Download className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </a>
          <div className="flex items-center gap-1 shrink-0">
            <label htmlFor={inputId} className="cursor-pointer text-xs font-medium text-amber-700 hover:underline">
              {uploading ? 'Uploading…' : 'Replace'}
            </label>
            <button type="button" onClick={onClear} className="p-1 text-amber-700 hover:bg-amber-100 rounded" aria-label={`Remove ${label}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <label htmlFor={inputId} className="flex items-center gap-1.5 text-sm text-amber-800 dark:text-amber-300 cursor-pointer hover:underline">
          <Upload className="h-4 w-4" />
          {uploading ? 'Uploading…' : `Upload ${label.match(/\(([^)]+)\)/)?.[1] || 'file'}`}
        </label>
      )}
      <input
        id={inputId}
        type="file"
        accept=".pdf,.doc,.docx"
        className="hidden"
        disabled={uploading}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
